import React, { useMemo, useContext } from 'react';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { Calendar } from 'primereact/calendar';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { useToast } from 'context/ToastContext';
import { toastConstant } from 'constants/toastmessage';
import { paymentMethods } from 'constants/dropdownOptions';
import { ModalHeader } from 'shared/ModalHeader';
import TextSnippetWrapper from 'components/TextSnippetWrapper';

import { useForm, Controller } from 'react-hook-form';
import { RequiredValidation } from 'components/UI/RequiredValidation/RequiredValidation';
import { disburseTrustFund } from 'services/billingServices';
import { confirmDialog } from 'primereact/confirmdialog';
import { setTimezoneWithoutAlteringTime } from 'utils/utility_functions/timezone';
import { UserDetailsContext } from 'context/userDetailsContext';
const getContactDisplayName = (contact) => {
  if (contact?.contact_type === 'person') {
    return [contact?.first_name, contact?.last_name].filter(Boolean).join(' ');
  }
  return contact?.title || contact?.first_name || '';
};

const formatGroupedContacts = (caseDetails) => {
  const allContacts = [...(caseDetails?.client_details || []), ...(caseDetails?.relations || [])];

  const seen = new Set();
  const uniqueContacts = allContacts?.filter((c) => {
    if (!c?.contact_id || seen.has(c?.contact_id)) return false;
    seen.add(c?.contact_id);
    return true;
  });

  const groups = { person: [], business: [], location: [] };
  uniqueContacts?.forEach((contact) => {
    const type = contact?.contact_type || 'person';
    if (groups[type]) {
      groups[type].push({
        contact_id: contact?.contact_id,
        name: getContactDisplayName(contact),
        first_name: contact?.first_name,
        is_confirmed: contact?.is_confirmed,
      });
    }
  });

  const sortByName = (a, b) => (a?.name || '').toLowerCase().localeCompare((b?.name || '').toLowerCase());
  const groupLabels = { person: 'Person', business: 'Business', location: 'Location' };

  return Object.entries(groups)
    .filter(([, items]) => items?.length > 0)
    .map(([key, items]) => ({
      label: groupLabels[key],
      items: items.sort(sortByName),
    }));
};

export const DisburseFundModal = ({ onHide, caseDetails, callback, availableBalance: availableBalanceProp }) => {
  const { addToast } = useToast();
  const userContext = useContext(UserDetailsContext);

  const availableBalance = availableBalanceProp != null ? parseFloat(availableBalanceProp) : 0;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      amount: undefined,
      refundDate: undefined,
      paymentMethod: undefined,
      notes: '',
    },
  });

  const groupedContacts = useMemo(() => formatGroupedContacts(caseDetails), [caseDetails]);

  const onChangeRefundFull = (e) => {
    if (e.target.checked && availableBalance > 0) {
      setValue('amount', availableBalance, { shouldValidate: true });
    }
    if (!e.target.checked) {
      setValue('amount', undefined);
    }
  };

  const onSubmit = (formData) => {
    confirmDialog({
      message: 'Editing and refunding options will be disabled for all trust transactions. Are you sure you want to proceed?',
      header: 'Warning',
      icon: 'pi pi-exclamation-triangle',
      accept: () => {
        const data = {
          case_id: caseDetails?.case_id,
          case_name: caseDetails?.case_description?.case_name,
          billing_contact_id: formData?.billing_contact?.contact_id,
          billing_contact_name: formData?.billing_contact?.name,
          description: formData?.notes,
          deposit_into: '',
          debit_account_name: caseDetails?.default_trust_account?.[0]?.account_name,
          debit_account_id: caseDetails?.default_trust_account?.[0]?.account_id,
          payment_record_amount: formData?.amount,
          payment_record_date: setTimezoneWithoutAlteringTime(formData?.refundDate, userContext?.userDetails?.timezone),
          payment_method: formData?.paymentMethod,
          payment_record_type: 'REFUND',
        };
        disburseTrustFund(data)
          .then((response) => {
            if (response?.status === 200) {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DISBURSE_FUND_SUCCESS);
              typeof callback === 'function' && callback(true);
              onHide();
            } else {
              addToast(false, toastConstant.toasterType.SUCCESS, toastConstant.api.SUCCESS, toastConstant.message.DISBURSE_FUND_FAILED);
            }
          })
          .catch((error) => {
            console.log('error', error);
            addToast(
              false,
              toastConstant.toasterType.ERROR,
              toastConstant.api.FAILED,
              error?.response?.data?.message || toastConstant.message.DISBURSE_FUND_FAILED
            );
          });
      },
      reject: () => null,
    });
  };

  const amount = watch('amount');

  return (
    <Dialog header={ModalHeader} visible onHide={onHide} className="create-msg-popup" closable={false}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="row">
          <div className="d-flex justify-content-between align-items-center mt-3 mb-3">
            <div className="modal-heading F-size16 text-bold">Withdraw Fund</div>
          </div>

          <div className="d-flex py-2 flex-wrap">
            <div className="col-12 col-md-4 pt-2">
              Withdraw Amount<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="amount"
                control={control}
                rules={{
                  required: 'Required Valid Amount',
                  min: { value: 0, message: 'Must be greater than 0' },
                  max: {
                    value: availableBalance,
                    message: 'Cannot be greater than available trust balance',
                  },
                }}
                render={({ field: { name, onChange, value } }) => (
                  <InputNumber
                    name={name}
                    value={value}
                    onChange={(e) => onChange(e.value)}
                    className="input-shadow w-100"
                    mode="currency"
                    currency="USD"
                    locale="en-US"
                  />
                )}
              />
              <RequiredValidation field="amount" errors={errors} />
              <div className="mt-3">
                <span className="me-2">Withdraw full amount</span>
                <label className="switch">
                  <input type="checkbox" checked={amount === availableBalance && availableBalance > 0} onChange={onChangeRefundFull} />
                  <span className="slider round"></span>
                </label>
              </div>
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4 p-medium">
              Contact<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="billing_contact"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field }) => (
                  <Dropdown
                    className="w-100"
                    options={groupedContacts}
                    onChange={(e) => field.onChange(e.value)}
                    optionLabel="name"
                    optionGroupLabel="label"
                    optionGroupChildren="items"
                    filterBy="name"
                    placeholder={'Select Contact'}
                    value={field.value}
                    filter
                  />
                )}
              />
              <RequiredValidation field="billing_contact" errors={errors} />
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4 p-medium">
              Payment Method<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="paymentMethod"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field: { value, onChange } }) => (
                  <Dropdown value={value} onChange={onChange} className="input-shadow w-100 p-0" options={paymentMethods} filter />
                )}
              />
              <RequiredValidation field="paymentMethod" errors={errors} />
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4">
              Date<span className="text-danger">*</span>
            </div>
            <div className="col-12 col-md-8">
              <Controller
                name="refundDate"
                control={control}
                rules={{ required: 'Required' }}
                render={({ field: { value, onChange } }) => <Calendar value={value} onChange={onChange} className="input-shadow w-100" />}
              />
              <RequiredValidation field="refundDate" errors={errors} />
            </div>
          </div>

          <div className="d-flex align-items-center py-2 flex-wrap">
            <div className="col-12 col-md-4">Notes</div>
            <div className="col-12 col-md-8">
              <Controller
                name="notes"
                control={control}
                render={({ field: { value, onChange } }) => (
                  <TextSnippetWrapper
                    value={value}
                    onChange={onChange}
                    type="textarea"
                    className="input-shadow w-100 p-2"
                    autoComplete="off"
                  />
                )}
              />
            </div>
          </div>
        </div>
        <hr />
        <div className="row">
          <div className="col-12 d-flex justify-content-end mt-2">
            <Button className="p-button-secondary outline" label="Cancel" type="button" onClick={onHide} />
            <Button className="p-button-secondary ms-2" label="Withdraw" type="submit" />
          </div>
        </div>
      </form>
    </Dialog>
  );
};
